import React from 'react';
import { Box, Typography, Grid, Button, CircularProgress, Alert } from '@mui/material';
import LocationSelector from '../forms/LocationSelector';
import MonthPicker from '../forms/MonthPicker'; 
import ServiceDefinitionForm from '../forms/ServiceDefinitionForm';
import { ServiceDefinition, Location as AppLocation } from '../../models';
import { useSearchContext } from '../../contexts/SearchContext';

interface SearchPageProps {
  locationDetails: any; // Keep for now if LocationSelector needs it separately
  isLoading: boolean;
  error: string | null;
  handleLocationSelect: (location: AppLocation | null) => void; // Expect full object or null
  // onDateChange and onServicesChange are removed as we'll use context updaters
  handleSearch: () => void; // Search triggered without explicit params
}

const SearchPage: React.FC<SearchPageProps> = ({
  locationDetails,
  isLoading,
  error,
  handleLocationSelect, // Keep this prop for now, as it might involve more than just setting context
  handleSearch        // Keep this prop
}) => {
  // Get search parameters and update functions from context
  const { searchParams, updateDate, updateServices } = useSearchContext();

  // Extract values from searchParams for child components, providing defaults
  const selectedLocationId = searchParams?.location?.id ?? null;
  const services = searchParams?.services ?? [];
  
  // Create a Date object from the searchParams or use current date as default
  const selectedDate = searchParams?.dateOfInterest ?? new Date();

  return (
    <>
      <Box sx={{ mb: 3 }}>
         <Typography variant="h6" gutterBottom>
           Where and When
         </Typography>
         <Grid container spacing={2}>
           <Grid item xs={12} md={6}>
             <LocationSelector
               selectedLocationId={selectedLocationId} // Pass ID for selection state
               onLocationSelect={(location) => handleLocationSelect(location)} // Pass full object handler
             />
           </Grid>
           <Grid item xs={12} md={6}>
              {/* Month picker for selecting month and year */}
             <MonthPicker
               selectedDate={selectedDate}
               onDateChange={updateDate} // Use the updateDate function from context
             />
           </Grid>
         </Grid>
       </Box>

       <Box sx={{ mb: 3 }}>
         <ServiceDefinitionForm
           services={services} // Pass services from searchParams
           onServicesChange={updateServices} // Use the updateServices function from context
         />
       </Box>

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-start' }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSearch} // Call handleSearch directly
          disabled={isLoading || !selectedLocationId || services.length === 0}
        >
          {isLoading ? <CircularProgress size={24} /> : 'Search'}
        </Button>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
    </>
  );
};

export default SearchPage;
